<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import { computed } from 'vue'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Tag from '@/Components/Utils/Tag.vue'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatNumber } from '@/Composables/useFormatNumber'
import { useFormatTime } from '@/Composables/useFormatTime'

interface Stat {
    label: string
    value: number
    type?: string
}

interface MonthlySale {
    period: string
    label: string
    quantity: number
    amount: number
    orders_count: number
}

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    data: {
        id: number
        slug: string
        name: string
        code?: string
        created_at?: string
        creator?: string
    }
    stats: Record<string, Stat>
    products: {
        id: number
        slug: string
        sku: string
        name: string
        print_type_label?: string
        brands_count: number
    }[]
    monthly_sales: MonthlySale[]
}>()

const formatStat = (stat: Stat) => stat.type === 'currency' ? useFormatCurrency(stat.value) : useFormatNumber(stat.value)

const maxMonthlyAmount = computed(
    () => Math.max(...props.monthly_sales.map(month => month.amount), 0)
)

const barWidth = (amount: number) => maxMonthlyAmount.value > 0
    ? `${Math.max((amount / maxMonthlyAmount.value) * 100, amount > 0 ? 2 : 0)}%`
    : '0%'
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="grid gap-4 px-4 py-4 lg:grid-cols-3">
        <div class="h-fit overflow-hidden rounded-xl border border-sky-400">
            <div class="border-b border-gray-900/5 bg-gray-50 p-6">
                <div class="flex items-center gap-x-2 font-bold">
                    {{ data.name }}
                    <Tag v-if="data.code" :label="data.code" :theme="3" noHoverColor />
                </div>
                <div class="text-sm/6 text-gray-500">Kategori produk katalog</div>
            </div>

            <dl class="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6">
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Kode kategori</dt>
                    <dd class="text-right font-mono font-medium">
                        {{ data.code || '-' }}
                    </dd>
                </div>
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Dibuat pada</dt>
                    <dd class="text-right font-medium">{{ useFormatTime(data.created_at) }}</dd>
                </div>
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Dibuat oleh</dt>
                    <dd class="text-right font-medium">
                        <span v-if="data.creator">{{ data.creator }}</span>
                        <span v-else class="italic text-gray-400">tidak tercatat</span>
                    </dd>
                </div>
            </dl>

            <div class="grid grid-cols-3 divide-x divide-gray-100 border-t border-gray-100 text-center">
                <div v-for="(stat, key) in stats" :key="key" class="px-2 py-3">
                    <div class="text-lg font-semibold">{{ formatStat(stat) }}</div>
                    <div class="text-xs text-gray-400">{{ stat.label }}</div>
                </div>
            </div>
        </div>

        <div class="lg:col-span-2 flex flex-col gap-4">
            <section class="overflow-hidden rounded-xl border border-gray-200">
                <div class="border-b border-gray-900/5 bg-gray-50 px-6 py-3 font-semibold text-gray-700">
                    Penjualan per bulan
                </div>

                <ul class="divide-y divide-gray-100 text-sm">
                    <li v-for="month in monthly_sales" :key="month.period" class="px-6 py-3">
                        <div class="flex items-center justify-between gap-x-4">
                            <span class="w-20 shrink-0 text-gray-500">{{ month.label }}</span>
                            <div class="h-2 flex-1 rounded-full bg-gray-100">
                                <div class="h-2 rounded-full bg-sky-400" :style="{ width: barWidth(month.amount) }" />
                            </div>
                            <span class="w-32 shrink-0 text-right font-medium">{{ useFormatCurrency(month.amount) }}</span>
                        </div>
                        <div class="mt-1 pl-24 text-xs text-gray-400">
                            {{ useFormatNumber(month.quantity) }} pcs &middot; {{ month.orders_count }} pesanan
                        </div>
                    </li>
                </ul>
            </section>

            <section class="overflow-hidden rounded-xl border border-gray-200">
                <div class="border-b border-gray-900/5 bg-gray-50 px-6 py-3 font-semibold text-gray-700">
                    Produk dalam kategori
                </div>

                <div v-if="!products.length" class="px-6 py-6 text-center text-sm text-gray-400">
                    Belum ada produk pada kategori ini
                </div>

                <table v-else class="w-full text-sm">
                    <thead class="text-xs uppercase text-gray-400">
                        <tr>
                            <th class="px-6 py-2 text-left">Produk</th>
                            <th class="px-6 py-2 text-left">Jenis cetak</th>
                            <th class="px-6 py-2 text-right">Dipakai brand</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr v-for="product in products" :key="product.id">
                            <td class="px-6 py-3">
                                <Link :href="route('kasir.products.show', product.slug)" class="primaryLink font-medium">
                                    {{ product.name }}
                                </Link>
                                <div class="font-mono text-xs text-gray-400">{{ product.sku }}</div>
                            </td>
                            <td class="px-6 py-3">{{ product.print_type_label }}</td>
                            <td class="px-6 py-3 text-right">{{ useFormatNumber(product.brands_count) }}</td>
                        </tr>
                    </tbody>
                </table>
            </section>
        </div>
    </div>
</template>
